package com.milta.feature.route.presentation

import com.milta.core.auth.JwtParser
import com.milta.core.auth.TokenStorage
import com.milta.core.cache.LocalCacheStore
import com.milta.core.mode.AppMode
import com.milta.feature.route.domain.entity.DeliveryDraft
import com.milta.feature.route.domain.entity.GeocodeFailureReason
import com.milta.feature.route.domain.entity.GeocodeStatus
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * Retour 28/10 (brouillon, option C) — a route being built and not sent yet: what "Nouvelle tournée"
 * (Enterprise) / "Créer ma tournée" (driver) holds between visits. One per account and per mode:
 * switching mode never carries the deliveries over to the other one. [date] is "yyyy-MM-dd" (blank =
 * server today), [driverIds] / [loadingAddressId] are Enterprise only.
 */
data class RouteDraft(
    val deliveries: List<DeliveryDraft> = emptyList(),
    val date: String = "",
    val driverIds: Set<String> = emptySet(),
    val loadingAddressId: String? = null,
) {
    /** No delivery = no draft: nothing to keep, nothing to show. */
    val isEmpty: Boolean get() = deliveries.isEmpty()
    val packagesCount: Int get() = deliveries.sumOf { (it.packageCount ?: 1).coerceAtLeast(1) }
}

/** Date kept by a new entry point: an existing draft keeps its own, an empty one takes [requested]. */
fun RouteDraft.dateForNewEntry(requested: String): String = if (isEmpty) requested else date

/** "dd/MM" from the draft's "yyyy-MM-dd", null when it has none (or an unexpected format). */
fun RouteDraft.dayMonthLabel(): String? {
    val parts = date.split("-")
    if (parts.size != 3 || parts[1].length != 2 || parts[2].length != 2) return null
    return "${parts[2]}/${parts[1]}"
}

/**
 * Persists [RouteDraft]s on disk ([LocalCacheStore], the key-value store already used for the offline
 * cache and the consumed imports), one key per account (JWT `sub`) and per [AppMode], so "Garder pour
 * plus tard" survives the app being killed. The keys written are listed under [INDEX_KEY] so
 * [clearAll] can drop them at logout, when the account id is no longer known.
 */
class RouteDraftStore(
    private val localCacheStore: LocalCacheStore,
    private val accountId: () -> String?,
) {
    constructor(localCacheStore: LocalCacheStore, tokenStorage: TokenStorage) : this(
        localCacheStore,
        { tokenStorage.getAccessToken()?.let(JwtParser::extractSubject) },
    )

    fun load(mode: AppMode): RouteDraft {
        val key = key(mode) ?: return RouteDraft()
        val raw = localCacheStore.getString(key) ?: return RouteDraft()
        return runCatching { json.decodeFromString(StoredRouteDraft.serializer(), raw).toDraft() }.getOrElse { RouteDraft() }
    }

    /** An empty draft is removed, not stored. */
    fun save(mode: AppMode, draft: RouteDraft) {
        val key = key(mode) ?: return
        if (draft.isEmpty) {
            localCacheStore.remove(key)
            return
        }
        localCacheStore.putString(key, json.encodeToString(StoredRouteDraft.serializer(), draft.toStored()))
        val index = indexedKeys()
        if (key !in index) localCacheStore.putString(INDEX_KEY, (index + key).joinToString(INDEX_SEPARATOR))
    }

    fun clear(mode: AppMode) = save(mode, RouteDraft())

    /** Logout: every draft written on this device goes, whatever the account. */
    fun clearAll() {
        indexedKeys().forEach(localCacheStore::remove)
        localCacheStore.remove(INDEX_KEY)
    }

    private fun key(mode: AppMode): String? = accountId()?.let { "$KEY_PREFIX$it:${mode.name}" }

    private fun indexedKeys(): Set<String> =
        localCacheStore.getString(INDEX_KEY)?.split(INDEX_SEPARATOR)?.filter { it.isNotBlank() }?.toSet() ?: emptySet()

    private companion object {
        const val KEY_PREFIX = "milta_route_draft:"
        const val INDEX_KEY = "milta_route_draft_keys"
        const val INDEX_SEPARATOR = ","
        val json = Json { ignoreUnknownKeys = true }
    }
}

@Serializable
private data class StoredRouteDraft(
    val deliveries: List<StoredDeliveryDraft> = emptyList(),
    val date: String = "",
    val driverIds: List<String> = emptyList(),
    val loadingAddressId: String? = null,
)

@Serializable
private data class StoredDeliveryDraft(
    val recipientName: String = "",
    val address: String = "",
    val phoneNumber: String? = null,
    val deadlineTime: String? = null,
    val packageCount: Int? = null,
    val geocodeStatus: String? = null,
    val latitude: Double? = null,
    val longitude: Double? = null,
    val geocodeFailureReason: String? = null,
    val resolvedAddress: String? = null,
)

private fun RouteDraft.toStored() = StoredRouteDraft(
    deliveries = deliveries.map {
        StoredDeliveryDraft(
            recipientName = it.recipientName,
            address = it.address,
            phoneNumber = it.phoneNumber,
            deadlineTime = it.deadlineTime,
            packageCount = it.packageCount,
            geocodeStatus = it.geocodeStatus.name,
            latitude = it.latitude,
            longitude = it.longitude,
            geocodeFailureReason = it.geocodeFailureReason?.name,
            resolvedAddress = it.resolvedAddress,
        )
    },
    date = date,
    driverIds = driverIds.toList(),
    loadingAddressId = loadingAddressId,
)

private fun StoredRouteDraft.toDraft() = RouteDraft(
    deliveries = deliveries.map { stored ->
        DeliveryDraft(
            recipientName = stored.recipientName,
            address = stored.address,
            phoneNumber = stored.phoneNumber,
            deadlineTime = stored.deadlineTime,
            packageCount = stored.packageCount,
            geocodeStatus = GeocodeStatus.entries.firstOrNull { it.name == stored.geocodeStatus } ?: GeocodeStatus.Pending,
            latitude = stored.latitude,
            longitude = stored.longitude,
            geocodeFailureReason = GeocodeFailureReason.entries.firstOrNull { it.name == stored.geocodeFailureReason },
            resolvedAddress = stored.resolvedAddress,
        )
    },
    date = date,
    driverIds = driverIds.toSet(),
    loadingAddressId = loadingAddressId,
)
